import { Injectable } from "@angular/core";
import { ApiService } from "@core/service/api.service";

import { Observable, throwError } from "rxjs";
import { map, catchError, first } from "rxjs/operators";

import { IEntity } from "@schema/entity";
import { ISerializes } from "@schema/serializable";

import { IHttpRequestOptions, IHttpResponse, IHttpApiResponse, IHttpErrorResponse } from "@schema/http";

@Injectable({
  providedIn: "root"
})
export abstract class BaseCrudService<TEntity extends IEntity, TEntityImport, TEntityExport> {

  readonly abstract baseUri: string;
  protected abstract endpoint: string;
  protected abstract readonly apiVersion: string;

  constructor(
    protected _apiService: ApiService,
  ) { }

  /**
   * Return all entities
   */
  protected _all(uri: string, options?: IHttpRequestOptions): Observable<IHttpResponse<TEntity[]>> {
    return this._apiService.get<TEntityImport[]>(uri, options)
      .pipe(
        first(),
        map((httpResponse: IHttpResponse<TEntityImport[]>) => {
          return {
            data: httpResponse.data.map(this._deserializeEntity),
            pagination: httpResponse.pagination,
          };
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Create a new Entity
   */
  protected _create<TNewEntity extends ISerializes<TEntityExport>>(
    entity: TNewEntity,
    uri: string,
    options?: IHttpRequestOptions
  ): Observable<TEntity> {
    return this._apiService.post(uri, entity.toJSON(), options).pipe(
      first(),
      catchError(this.handleError),
      map((httpResponse: IHttpResponse<TEntityImport>): TEntity => {
        return this._deserializeEntity(httpResponse.data);
      }),
    );
  }

  /**
   * Find an entity based on an ID
   */
  protected _find(uri: string, options?: IHttpRequestOptions): Observable<TEntity> {
    return this._apiService.get<TEntityImport>(uri, options)
      .pipe(
        first(),
        catchError(this.handleError),
        map((httpResponse: IHttpResponse<TEntityImport>): TEntity => {
          return this._deserializeEntity(httpResponse.data);
        }),
      );
  }

  /**
   * Create a new Entity
   */
  protected _update<TUpdatedEntity extends ISerializes<TEntityExport>>(
    entity: TUpdatedEntity,
    uri: string,
    options?: IHttpRequestOptions
  ): Observable<TEntity> {
    return this._apiService.put(uri, entity.toJSON(), options).pipe(
      first(),
      catchError(this.handleError),
      map((httpResponse: IHttpResponse<TEntityImport>): TEntity => {
        return this._deserializeEntity(httpResponse.data);
      }),
    );
  }

  /**
   * Delete an entity based on an ID
   */
  protected _delete(uri: string, options?: IHttpRequestOptions): Observable<boolean> {
    return this._apiService.delete<null>(uri, options)
      .pipe(
        first(),
        catchError(this.handleError),
        map((httpResponse: IHttpResponse<null>): boolean => {
          return httpResponse.status >= 200 && httpResponse.status < 300;
        }),
      );
  }

  /**
   * Enrich the error data
   */
  protected handleError(err: IHttpApiResponse<any>): Observable<any> {
    return throwError({
      status: err.status,
      message: err.message || "An uknown error has occured",
    } as IHttpErrorResponse);
  }

  /**
   * Deserialize an entity from JSON
   */
  protected abstract _deserializeEntity(json: TEntityImport): TEntity;
}
